// frontend/src/pages/DashboardPage.tsx
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RecentActivity } from '@redpanda/types';
import { useDashboardStore } from '../stores/dashboardStore';
import { useTaskStore } from '../stores/taskStore';
import { useCustomerStore } from '../stores/customerStore';
import { useOpportunityStore } from '../stores/opportunityStore';
import { useQuoteStore } from '../stores/quoteStore';
import { useProjectStore } from '../stores/projectStore';
import { usePaymentStore } from '../stores/paymentStore';
import { useTagStore } from '../stores/tagStore';
import { StatusDot, Amount, StatCard } from '@redpanda/ui';
import ActivityItem from '../components/ActivityItem';
import { priorityTone, toneOf } from '../utils/statusTone';
import { formatTableDate } from '@redpanda/utils';

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { stats, error, fetchDashboardStats } = useDashboardStore();
  const { upcomingTasks } = useTaskStore();

  // 預載入所有頁面的 stores
  const customerStore = useCustomerStore();
  const opportunityStore = useOpportunityStore();
  const quoteStore = useQuoteStore();
  const projectStore = useProjectStore();
  const taskStore = useTaskStore();
  const paymentStore = usePaymentStore();
  const tagStore = useTagStore();

  // 🚨 修正：移除 fetchDashboardStats 依賴，避免無限循環
  // 使用 forceRefresh 確保每次進入儀表板都能看到最新資料
  useEffect(() => {
    fetchDashboardStats(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 預載入所有頁面的第一頁快取
  // 🚨 修正：移除 store 依賴，避免無限循環
  useEffect(() => {
    const preloadAllFirstPages = () => {
      const stores = [
        { store: taskStore, name: 'tasks', fetchMethod: 'fetchUpcomingTasks' }, // 儀表板需要的任務數據
        { store: customerStore, name: 'customers', fetchMethod: 'fetchCustomers' },
        { store: opportunityStore, name: 'opportunities', fetchMethod: 'fetchOpportunities' },
        { store: quoteStore, name: 'quotes', fetchMethod: 'fetchQuotes' },
        { store: projectStore, name: 'projects', fetchMethod: 'fetchProjects' },
        { store: paymentStore, name: 'payments', fetchMethod: 'fetchPayments' },
        { store: tagStore, name: 'tags', fetchMethod: 'fetchTags' }
      ];

      // 分批載入，避免同時發送所有請求 - 完全背景執行
      stores.forEach(({ store, name, fetchMethod }, index) => {
        setTimeout(() => {
          try {
            // 檢查該 store 是否已有第一頁快取（tasks 沒有分頁，一律重新抓）
            const hasCache = name !== 'tasks' && (store as any).pagination !== null;

            // 🚨 修正：只在沒有快取（pagination === null）且沒有錯誤狀態時才預載入
            // 避免在 API 失敗後不斷重試
            const hasError = (store as any).error !== null && (store as any).error !== undefined;

            if (!hasCache && !hasError) {
              // 背景載入第一頁，不強制刷新
              if (name === 'tasks') {
                // 任務使用特殊方法，不需要頁數參數
                (store as any)[fetchMethod]();
              } else {
                (store as any)[fetchMethod](1, false);
              }
            }
          } catch (error) {
            // 預載入失敗不影響主功能
            console.debug(`Preload ${name} failed:`, error);
          }
        }, index * 500); // 增加間隔到 500ms，減少並發壓力
      });
    };

    // 延遲 3 秒後開始預載入，確保儀表板先載入完成
    // 並且只執行一次
    const timer = setTimeout(() => {
      preloadAllFirstPages();
    }, 3000);

    // Cleanup function
    return () => clearTimeout(timer);
  }, []); // 🚨 修正：空依賴陣列，只在初次載入時執行一次

  const isToday = (dateString: string) => {
    const today = new Date();
    const taskDate = new Date(dateString);
    return today.toDateString() === taskDate.toDateString();
  };

  const isOverdue = (dateString: string, status: string) => {
    if (status === 'COMPLETED') return false;
    const today = new Date();
    const taskDate = new Date(dateString);
    // 逾期定義：到期日早於今天（不包括今天）
    today.setHours(0, 0, 0, 0);
    taskDate.setHours(0, 0, 0, 0);
    return taskDate < today;
  };

  // 待辦：後端 /tasks/upcoming 只回傳「逾期＋今日到期」的未完成任務；逾期排前面
  const overdueTasks = (upcomingTasks || []).filter(task => isOverdue(task.dueDate, task.status));
  const todayTasks = (upcomingTasks || []).filter(task => isToday(task.dueDate) && task.status !== 'COMPLETED');
  const dueTasks = [...overdueTasks, ...todayTasks];
  const DUE_LIST_LIMIT = 6;

  const percent = (value?: number) => `${value ?? 0}%`;

  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }
  

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('dashboard.title', { ns: 'pages' })}</h1>
      </div>

      {/* 每天要看的 4 個數字（§3-4：一列最多 4 張；活躍客戶數、本月互動次數移到各列表／報表） */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t('dashboard.monthlyRevenue', { ns: 'pages' })}
          value={<Amount value={stats?.monthlyRevenue ?? 0} />}
        />
        <StatCard
          title={t('dashboard.pendingQuoteValue', { ns: 'pages' })}
          value={<Amount value={stats?.pendingQuoteValue ?? 0} />}
        />
        <StatCard
          title={t('dashboard.newCustomersMonth', { ns: 'pages' })}
          value={stats?.newCustomersThisMonth ?? 0}
        />
        <StatCard
          title={t('dashboard.inProgressProjects', { ns: 'pages' })}
          value={stats?.inProgressProjects ?? 0}
        />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* 待辦清單：取代 3 張任務卡＋「太棒了」區塊 */}
        <section className="lg:col-span-2">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base font-semibold text-fg">{t('dashboard.taskReminders.title', { ns: 'pages' })}</h2>
            <Link to="/tasks" className="text-sm text-accent hover:underline">
              {t('dashboard.taskReminders.viewAllTasks', { ns: 'pages' })}
            </Link>
          </div>
          <div className="bg-surface rounded-lg border border-line overflow-hidden">
            <div className="px-5 py-3 border-b border-line text-[13px] text-fg-secondary tabular-nums">
              <span className={overdueTasks.length > 0 ? 'text-red-600 dark:text-red-400' : ''}>
                {t('dashboard.taskReminders.overdue', { ns: 'pages' })} {overdueTasks.length}
              </span>
              <span className="mx-2 text-fg-muted">·</span>
              <span>{t('dashboard.taskReminders.dueToday', { ns: 'pages' })} {todayTasks.length}</span>
            </div>
            {dueTasks.length === 0 ? (
              <p className="px-5 py-6 text-sm text-fg-secondary">{t('dashboard.taskReminders.nothingDue', { ns: 'pages' })}</p>
            ) : (
              <ul className="divide-y divide-line">
                {dueTasks.slice(0, DUE_LIST_LIMIT).map(task => {
                  const overdue = isOverdue(task.dueDate, task.status);
                  return (
                    <li key={task.id} className="px-5 py-3 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-fg truncate">{task.title}</p>
                        {task.customer?.name && (
                          <Link to={`/customers/${task.customerId}`} className="text-[13px] text-fg-secondary hover:text-accent transition-colors">
                            {task.customer.name}
                          </Link>
                        )}
                      </div>
                      <StatusDot tone={toneOf(priorityTone, task.priority)} label={t(`tasks.priority.${task.priority}`, { ns: 'pages' })} />
                      <span className={`w-28 text-right text-sm tabular-nums ${overdue ? 'text-red-600 dark:text-red-400' : 'text-fg-secondary'}`}>
                        {overdue
                          ? `${t('dashboard.taskReminders.taskDetails.overdue', { ns: 'pages' })} ${formatTableDate(task.dueDate, i18n.language)}`
                          : t('dashboard.taskReminders.dueToday', { ns: 'pages' })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {dueTasks.length > DUE_LIST_LIMIT && (
              <div className="px-5 py-3 border-t border-line">
                <Link to="/tasks" className="text-sm text-accent hover:underline">
                  {t('dashboard.taskReminders.taskDetails.viewAllUrgent', { ns: 'pages', total: dueTasks.length })}
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* 業績轉換：5 張卡改成一個容器的「標籤：值」清單 */}
        <section>
          <h2 className="text-base font-semibold text-fg mb-3">{t('dashboard.quoteAnalysis.title', { ns: 'pages' })}</h2>
          <dl className="bg-surface rounded-lg border border-line divide-y divide-line text-sm">
            {[
              { label: t('dashboard.quoteAnalysis.quantityConversionRate', { ns: 'pages' }), value: percent(stats?.quotesToProjectsRatio) },
              { label: t('dashboard.quoteAnalysis.amountConversionRate', { ns: 'pages' }), value: percent(stats?.quoteValueConversionRatio) },
              { label: t('dashboard.quoteAnalysis.totalQuoteValue', { ns: 'pages' }), value: <Amount value={stats?.totalQuoteValue ?? 0} /> },
              { label: t('dashboard.quoteAnalysis.convertedQuoteValue', { ns: 'pages' }), value: <Amount value={stats?.convertedQuoteValue ?? 0} /> },
              { label: t('dashboard.quoteAnalysis.unconvertedQuoteValue', { ns: 'pages' }), value: <Amount value={stats?.unconvertedQuoteValue ?? 0} /> },
            ].map(row => (
              <div key={row.label} className="flex items-center justify-between px-5 py-3">
                <dt className="text-fg-secondary">{row.label}</dt>
                <dd className="font-medium text-fg tabular-nums">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section className="mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-base font-semibold text-fg">{t('dashboard.recentActivity', { ns: 'pages' })}</h2>
          <Link to="/activities" className="text-sm text-accent hover:underline">
            {t('dashboard.viewAll', { ns: 'pages' })}
          </Link>
        </div>
        <div className="bg-surface rounded-lg border border-line overflow-hidden">
          <ul className="divide-y divide-line">
            {stats?.recentActivities && stats.recentActivities.length > 0 ? (
              stats.recentActivities.map((activity: RecentActivity) => (
                <ActivityItem key={`${activity.type}-${activity.id}`} activity={activity} />
              ))
            ) : (
              <li className="px-5 py-6 text-sm text-fg-secondary">
                {t('dashboard.noRecentActivity', { ns: 'pages' })}
              </li>
            )}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
